/**
 * AdminDashboardPage — Executive admin dashboard with KPIs and activity feed.
 */

import React from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Activity,
  AlertTriangle,
  Building2,
  Key,
  Layers,
  TrendingDown,
  TrendingUp,
  Users,
  Workflow,
  Zap,
} from 'lucide-react';
import { AdminConsoleService } from '../../../services/adminConsoleService';
import { getSaasMetrics } from '../../../services/workspaceService';
import { RequireCapability } from '../../../components/auth/RequireCapability';
import { AdminConsoleLayout } from '../components/AdminConsoleLayout';
import { ModuleCard, ModuleLoadingState } from '../../shared/components/ModulePageHeader';

const currency = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' });
const fmt = (n: number) => n.toLocaleString('pt-BR');
const relTime = (iso: string) => {
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return 'agora';
  if (m < 60) return `${m}m atrás`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h atrás`;
  return `${Math.floor(h / 24)}d atrás`;
};

const KpiCard: React.FC<{
  label: string;
  value: string;
  icon: React.ComponentType<any>;
  sub?: string;
  mono?: boolean;
}> = ({ label, value, icon: Icon, sub, mono = false }) => (
  <div className="enterprise-card-surface p-[var(--card-padding)] flex flex-col gap-2">
    <div className="flex items-center justify-between">
      <span className="enterprise-field-label text-[11px]">{label}</span>
      <Icon size={14} className="enterprise-text-secondary opacity-60" />
    </div>
    <div className={`text-2xl font-semibold enterprise-text-primary tracking-tight ${mono ? 'font-mono tabular-nums' : ''}`}>
      {value}
    </div>
    {sub && <span className="text-[11px] enterprise-text-secondary">{sub}</span>}
  </div>
);

export const AdminDashboardPage: React.FC = () => {
  const statsQuery = useQuery({
    queryKey: ['admin-dashboard-extended'],
    queryFn: () => AdminConsoleService.getDashboardStatsExtended(),
  });

  const saasQuery = useQuery({
    queryKey: ['admin-saas-metrics'],
    queryFn: getSaasMetrics,
    staleTime: 60_000,
    retry: false,
  });

  const stats = statsQuery.data;
  const saas = saasQuery.data;

  return (
    <RequireCapability capability="admin.access" realRoleOnly>
      <AdminConsoleLayout>
        <div className="enterprise-page h-full overflow-y-auto">

          {statsQuery.isPending && <ModuleLoadingState label="Carregando métricas" />}

          {!statsQuery.isPending && (
            <>
              {/* KPI Row */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-[var(--block-gap)]">
                <KpiCard
                  label="Organizações"
                  value={fmt(stats?.activeOrganizations ?? 0)}
                  icon={Building2}
                  sub={`${stats?.totalClients ?? 0} total`}
                />
                <KpiCard
                  label="Usuários Ativos"
                  value={fmt(stats?.totalActiveUsers ?? 0)}
                  icon={Users}
                  sub={`de ${fmt(stats?.totalUsers ?? 0)} cadastrados`}
                />
                <KpiCard
                  label="Licenças Ativas"
                  value={fmt(stats?.activeClients ?? 0)}
                  icon={Key}
                  sub={`${stats?.licensesExpiringSoon ?? 0} expiram em 30d`}
                />
                <KpiCard
                  label="MRR"
                  value={currency.format(stats?.mrr ?? 0)}
                  icon={TrendingUp}
                  mono
                  sub="Receita Recorrente Mensal"
                />
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-[var(--block-gap)]">
                <KpiCard
                  label="Trial"
                  value={fmt(stats?.trialClients ?? 0)}
                  icon={Zap}
                />
                <KpiCard
                  label="Suspensos"
                  value={fmt(stats?.suspendedClients ?? 0)}
                  icon={Activity}
                />
                <KpiCard
                  label="Expirados"
                  value={fmt(stats?.expiredClients ?? 0)}
                  icon={Activity}
                />
                <KpiCard
                  label="Workflows Ativos"
                  value={fmt(stats?.totalWorkflows ?? 0)}
                  icon={Workflow}
                />
              </div>

              {/* SaaS Workspaces Section */}
              <div className="flex items-center gap-2 mt-2 mb-0.5">
                <Layers size={13} className="enterprise-text-secondary" />
                <span className="text-[11px] font-semibold uppercase tracking-[0.06em] enterprise-text-secondary">
                  SaaS — Workspaces
                </span>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-[var(--block-gap)]">
                <KpiCard
                  label="Total Workspaces"
                  value={fmt(saas?.total_workspaces ?? 0)}
                  icon={Layers}
                />
                <KpiCard
                  label="Em Trial"
                  value={fmt(saas?.trial_workspaces ?? 0)}
                  icon={Zap}
                  sub={`${saas?.expired_trials ?? 0} trials expirados`}
                />
                <KpiCard
                  label="Ativos (pagos)"
                  value={fmt(saas?.active_workspaces ?? 0)}
                  icon={Building2}
                />
                <KpiCard
                  label="MRR Total"
                  value={currency.format(saas?.total_mrr ?? 0)}
                  icon={TrendingUp}
                  mono
                  sub="Receita mensal consolidada"
                />
              </div>

              {/* SaaS Revenue & Health */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-[var(--block-gap)]">
                <KpiCard
                  label="ARR"
                  value={currency.format(saas?.total_arr ?? 0)}
                  icon={TrendingUp}
                  mono
                  sub="Receita anual recorrente"
                />
                <KpiCard
                  label="Conversão Trial→Pago"
                  value={(() => {
                    const paid = saas?.paid_subscriptions ?? 0;
                    const trial = saas?.trial_subscriptions ?? 0;
                    const total = paid + trial;
                    return total > 0 ? `${Math.round((paid / total) * 100)}%` : '—';
                  })()}
                  icon={Zap}
                  sub={`${saas?.paid_subscriptions ?? 0} pagos / ${saas?.trial_subscriptions ?? 0} trials`}
                />
                <KpiCard
                  label="Churn 30d"
                  value={fmt(saas?.churn_30d ?? 0)}
                  icon={TrendingDown}
                  sub="Cancelados nos últimos 30 dias"
                />
                <KpiCard
                  label="Alertas / Erros"
                  value={`${fmt(saas?.active_alerts ?? 0)} / ${fmt(saas?.errors_24h ?? 0)}`}
                  icon={AlertTriangle}
                  sub="Alertas ativos · Erros 24h"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-[var(--block-gap)]">
                {/* License Distribution */}
                <ModuleCard title="Distribuição de Status">
                  <div className="space-y-2">
                    {[
                      { label: 'Ativas', count: stats?.activeClients ?? 0, color: 'bg-emerald-500' },
                      { label: 'Trial', count: stats?.trialClients ?? 0, color: 'bg-amber-400' },
                      { label: 'Suspensas', count: stats?.suspendedClients ?? 0, color: 'bg-red-400' },
                      { label: 'Expiradas', count: stats?.expiredClients ?? 0, color: 'bg-slate-400' },
                    ].map(({ label, count, color }) => {
                      const total = stats?.totalClients ?? 1;
                      const pct = total > 0 ? Math.round((count / total) * 100) : 0;
                      return (
                        <div key={label}>
                          <div className="flex justify-between text-[11px] enterprise-text-secondary mb-1">
                            <span>{label}</span>
                            <span className="font-mono">{count} ({pct}%)</span>
                          </div>
                          <div className="h-1.5 rounded-full bg-enterprise-border-soft overflow-hidden">
                            <div
                              className={`h-full rounded-full ${color} transition-all`}
                              style={{ width: `${pct}%` }}
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </ModuleCard>

                {/* Activity Feed */}
                <div>
                  <h3 className="text-[12px] font-semibold uppercase tracking-[0.06em] enterprise-text-secondary mb-3">Atividade Recente</h3>
                  {(!stats?.recentActivity?.length) ? (
                    <p className="text-[12px] enterprise-text-secondary">Sem atividade registrada.</p>
                  ) : (
                    <div className="space-y-2">
                      {stats.recentActivity.map((item) => (
                        <div key={item.id} className="flex items-start gap-2 py-1 border-b border-enterprise-border-soft last:border-0">
                          <span className="mt-0.5 h-1.5 w-1.5 rounded-full bg-enterprise-primary shrink-0 mt-[5px]" />
                          <div className="min-w-0 flex-1">
                            <p className="text-[12px] enterprise-text-primary leading-tight">
                              <span className="font-medium">{item.user_name}</span>{' '}
                              <span className="enterprise-text-secondary">{item.action}</span>{' '}
                              {item.resource && <span className="font-mono text-[11px]">"{item.resource}"</span>}
                            </p>
                          </div>
                          <span className="text-[10px] enterprise-text-secondary shrink-0 tabular-nums">
                            {relTime(item.created_at)}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      </AdminConsoleLayout>
    </RequireCapability>
  );
};
